// ProjectDisplay.js
import { useState } from 'react';
import useTypewriter from '../hooks/useTypewriter.js';

/**
 * ProjectDisplay Component
 * A reusable project viewer that dynamically switches between Overview, Features, Tech, and Links.
 * 
 * @param {Object} props
 * @param {Object} props.projectData - The data describing the project
 */
function ProjectDisplay({ projectData }) {
  const [activeSection, setActiveSection] = useState('overview');
  const sections = ['overview', 'features', 'tech', 'links'];

  // --- Animated text setup ---
  const animatedName = useTypewriter(projectData.name, 40, 0);
  const animatedTagline = useTypewriter(projectData.tagline, 30, 1000);
  const animatedOverview = useTypewriter(projectData.overview, 20, 2000);

  return (
    <div className="project-container">
      {/* Header */}
      <div className="project-header">
        <h1>{animatedName}</h1>
        <h3>{animatedTagline}</h3>
        <span>
          ● {projectData.status} | {projectData.version}
        </span>
      </div>

      {/* Menu */}
      <div className="section-menu">
        {sections.map((section) => (
          <button
            key={section}
            onClick={() => setActiveSection(section)}
            className={`menu-button ${activeSection === section ? 'active' : ''}`}
          >
            ./{section}
          </button>
        ))}
      </div>

      {/* Content Sections */}
      <div className="section-content">
        {activeSection === 'overview' && (
          <div>
            <p>{animatedOverview}</p>
          </div>
        )}

        {activeSection === 'features' && (
          <div>
            {projectData.features?.map((feature, idx) => (
              <div key={idx}>
                <span>▸</span> <span>{feature}</span>
              </div>
            ))}
          </div>
        )}

        {activeSection === 'tech' && (
          <div>
            {projectData.tech?.map((tech, idx) => (
              <div key={idx}>
                <span>▸</span> <span>{tech}</span>
              </div>
            ))}
          </div>
        )}

        {activeSection === 'links' && (
          <div>
            {Object.entries(projectData.links || {}).map(([key, url]) => (
              <div key={key}>
                <span>{key}: </span>
                <a href={url} target="_blank" rel="noopener noreferrer">
                  {url}
                </a>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default ProjectDisplay;
